import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Truck, Recycle, ArrowRight, Camera } from "lucide-react";
import { api } from "../lib/api";
import { ItemCard } from "../components/ItemCard";

function MobileFeed() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data.filter((c) => !c.parent_id))).catch(() => {});
  }, []);

  const load = useCallback(async (p, catId, replace = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { page: p, limit: 12, ...(catId ? { category_id: catId } : {}) } });
      setItems((prev) => {
        const base = replace ? [] : prev;
        const seen = new Set(base.map((i) => i.id));
        return [...base, ...data.items.filter((i) => !seen.has(i.id))];
      });
      setPage(data.page);
      setPages(data.pages);
    } catch {} finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1, activeCat, true); }, [activeCat, load]);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && page < pages && !loadingRef.current) load(page + 1, activeCat);
    }, { rootMargin: "600px" });
    if (sentinelRef.current) obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [page, pages, activeCat, load]);

  return (
    <div className="md:hidden" data-testid="mobile-home-feed">
      <div className="px-3 py-3">
        <div className="grid grid-cols-2 gap-3">
          {items.map((it, i) => <ItemCard key={it.id} item={it} index={i % 12} />)}
          {loading && [...Array(4)].map((_, i) => (
            <div key={`sk-${i}`}>
              <div className="aspect-[2/3] bg-muted animate-pulse rounded-xl" />
              <div className="pt-2 space-y-2">
                <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
                <div className="h-4 bg-muted rounded animate-pulse w-1/3" />
              </div>
            </div>
          ))}
        </div>
        {!loading && items.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-14" data-testid="mobile-feed-empty">Keine Artikel in dieser Kategorie.</p>
        )}
        {!loading && page >= pages && items.length > 0 && (
          <p className="text-center text-xs text-muted-foreground py-6">Du hast alle Artikel gesehen.</p>
        )}
        <div ref={sentinelRef} className="h-1" />
      </div>
    </div>
  );
}

export default function Home() {
  const [feed, setFeed] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/home").then(({ data }) => setFeed(data)).catch(() => setFeed({ newest: [], popular: [], categories: [] }));
  }, []);

  return (
    <div>
      <MobileFeed />

      {/* ===== Desktop ===== */}
      <div className="hidden md:block">
      <section className="relative bg-primary text-primary-foreground overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">Kaufen. Verkaufen.<br /><span className="text-accent">Weitergeben.</span></h1>
            <p className="mt-5 text-primary-foreground/80 text-base max-w-lg">Kaufe und verkaufe Second-Hand — sicher bezahlt, mit Käuferschutz und automatischer Verkäufer-Auszahlung.</p>
            <form onSubmit={(e) => { e.preventDefault(); nav(`/suche?q=${encodeURIComponent(q)}`); }} className="mt-8 max-w-xl">
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Wonach suchst du?"
                  className="w-full rounded-full bg-white text-foreground pl-13 pr-32 py-4 text-sm sm:text-base focus:outline-none focus:ring-4 focus:ring-accent/40" style={{ paddingLeft: "3.25rem" }} />
                <button type="submit" data-testid="hero-search-btn"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-accent text-white font-bold text-sm px-6 py-2.5 rounded-full hover:opacity-90 active:scale-95 transition-transform">Suchen</button>
              </div>
            </form>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-primary-foreground/70">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-accent" /> Käuferschutz</span>
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-accent" /> Versand mit Tracking</span>
              <span className="flex items-center gap-2"><Recycle className="w-4 h-4 text-accent" /> Nachhaltig shoppen</span>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-2">
            <img src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=75" alt="Second-Hand Mode"
              className="rounded-3xl object-cover h-[420px] w-full border-4 border-white/10" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-lg font-bold mb-4">Kategorien</h2>
        <div className="flex gap-2.5 flex-wrap" data-testid="category-list">
          {(feed?.categories || []).map((c) => (
            <Link key={c.id} to={`/suche?category_id=${c.id}`} data-testid={`category-chip-${c.slug}`}
              className="px-4 py-2 rounded-full border border-border bg-card text-sm font-medium hover:bg-secondary hover:border-primary/30 transition-colors">
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Neu eingestellt</h2>
          <Link to="/suche" data-testid="see-all-newest" className="text-sm font-semibold text-primary flex items-center gap-1 hover:gap-2 transition-all">Alle ansehen <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {feed === null ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {[...Array(5)].map((_, i) => <div key={i} className="aspect-[4/5] bg-muted rounded-2xl animate-pulse" />)}
          </div>
        ) : feed.newest.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-12 text-center" data-testid="empty-newest">
            <p className="text-muted-foreground">Noch keine Artikel. Sei die erste Person, die etwas verkauft!</p>
            <Link to="/verkaufen" className="inline-block mt-4 bg-accent text-white font-bold text-sm px-6 py-3 rounded-full hover:opacity-90">Jetzt Artikel einstellen</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {feed.newest.map((it, i) => <ItemCard key={it.id} item={it} index={i} />)}
          </div>
        )}
      </section>

      {feed?.popular?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-16">
          <h2 className="text-lg font-bold mb-4">Beliebt</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {feed.popular.slice(0, 10).map((it, i) => <ItemCard key={it.id} item={it} index={i} />)}
          </div>
        </section>
      )}

      <section className="bg-secondary/60 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 text-center">
          <h2 className="text-lg md:text-lg font-bold">Aussortieren lohnt sich</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto text-sm">Stelle deine Artikel in wenigen Minuten ein. Nach dem Verkauf wird dein Geld automatisch ausgezahlt.</p>
          <Link to="/verkaufen" data-testid="cta-sell-bottom" className="inline-block mt-6 bg-primary text-primary-foreground font-bold text-sm px-8 py-3.5 rounded-full hover:opacity-90 active:scale-95 transition-transform">Artikel verkaufen</Link>
        </div>
      </section>
      </div>
    </div>
  );
}
